import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { FileUploadService } from '../../../../services.module/upload.service';
import { AppConstants } from '../../../../app-constants.service';
import { AuthService } from '../../../../services.module/auth.service';
import { MessageObservable } from '../../../../services.module/message.observable';
import { MessageModel } from '../../../../model.module/modals.model';
import { JahresabschlussApiService } from '../../../../../generated/api/jahresabschluss/jahresabschluss.service';
import {
    JahresabschlussFinishRequest,
    Notification,
    NotificationType
} from '../../../../../generated/api/kreditportalBackendAPI.schemas';


@Component({
    selector: 'app-jahresabschluss',
    templateUrl: './jahresabschluss.component.html',
    styleUrls: ['../../../commonless/content.less', '../../../commonless/meine-daten.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class JahresabschlussComponent implements AfterViewInit, OnDestroy {

    notifications: Notification[] = [
        //empty
    ];

    currentFileIndex = 0;

    uploadedPercent: number[] = [];

    done = false;

    running = false;

    fileQueue: File[] = [];

    @ViewChild('dragOverBox')
    dragOverBox: ElementRef;

    @ViewChild('fileInput')
    fileInput: ElementRef;

    private filenames: string[] = [];

    constructor(
        protected readonly authService: AuthService,
        protected readonly fileUploadService: FileUploadService,
        protected readonly appConstants: AppConstants,
        protected readonly messageHandler: MessageObservable,
        protected readonly jahresabschlussApiService: JahresabschlussApiService
    ) {
        this.fileUploadService = fileUploadService.forData(
            '/jahresabschluss',
            5,
            -1,
            appConstants.jahresAbschlussMaxFileSumSize,
            false,
            appConstants.jahresabschlussFilter,
            appConstants.messages.jahresabschlussFileTypeMsg,
            this.onProgress,
            this.onAdded,
            this.onUploaded,
            this.onError,
        );
    }

    ngAfterViewInit() {
        this.initDragOver();
    }

    ngOnDestroy() {
        this.fileQueue = [];
        this.fileUploadService.cleanup();
    }

    initDragOver() {
        const dropZone = this.dragOverBox.nativeElement;
        const fileZone = this.fileInput.nativeElement;

        dropZone.addEventListener('dragover', (evt: Event) => {
            this.handleDragOver(evt);
        }, false);

        dropZone.addEventListener('drop', (evt: Event) => {
            this.fileUploadService.addFiles(evt);
        }, false);

        fileZone.addEventListener('change', (evt: Event) => {
            this.fileUploadService.addFiles(evt);
        }, false);
    }

    startUpload() {
        this.notifications = [];

        // Prüfen, ob Sonderzeichen eingeben wurden, die nicht erlaubt sind
        for (const file of this.fileQueue) {
            const checkFileRegEx = new RegExp(AppConstants.VHV_UPLOAD_FILENAME_REGEX);

            if (!checkFileRegEx.test(file.name)) {
                this.onError(AppConstants.VHV_UPLOAD_FILENAME_MSG);
                return;
            }
        }
        this.fileQueue = this.fileQueue.slice(0);
        this.filenames = [];

        this.fileUploadService.startUpload();
        this.running = true;
    }

    deleteMessage = (i: number): void => {
        this.notifications.splice(i, 1);
    };

    deleteUpload = (i: number): void => {
        if (!this.running && !this.done) {
            this.fileUploadService.deleteUpload(i);
        }
    };

    private handleDragOver(evt: Event): void {
        evt.stopPropagation();
        evt.preventDefault();
        evt['dataTransfer'].dropEffect = 'copy';
    }

    private readonly onError = (error: string): void => {
        if (error === null) {
            return;
        }

        const notification: Notification = {
            message: error,
            type: NotificationType.error
        };

        this.notifications.push(notification);
        this.notifications = this.notifications.slice(0);
        this.running = false;
    };

    private readonly onUploaded = (filename: string): void => {
        // Das Backend schickt eine Info, wie die gehashte Datei heißt; übernehme dies
        this.filenames[this.currentFileIndex] = filename;

        ++this.currentFileIndex;

        // Alle Dateien hochgeladen
        if (this.currentFileIndex === this.fileQueue.length) {
            this.sendUploadFinish();
        }
    };

    /*
     * ACHTUNG, TESTEN!!! Wir brauchen hier wahrscheinlich den echten Kunden, also
     * auch den, der vom Makler vertreten wird.
     */
    private sendUploadFinish(): void {
        this.done = true;

        const request: JahresabschlussFinishRequest = {
            user: this.authService.getActiveKunde().vsnr,
            roleName: this.authService.getRoleName(),
            activeUser: this.authService.getUserId(),
            files: this.filenames
        }

        this.jahresabschlussApiService.postJahresabschlussFinish(request).subscribe({
            next: () => {
                console.debug('Jahresabschluss erfolgreich hochgeladen');
                this.currentFileIndex = 0;
                this.running = false;
                this.messageHandler.notify(new MessageModel('Ihr Jahresabschluss wurde erfolgreich hochgeladen.', []));
            },
            error: () => {
                this.running = false;
                this.fileQueue = [];
                this.uploadedPercent = [];
                this.fileUploadService.cleanup();
                this.currentFileIndex = 0;
            }
        });
    }

    private createZeroArray(size: number): number[] {
        const result: number[] = [];
        for (let i = 0; i < size; i++) {
            result.push(0);
        }
        return result;
    }

    private readonly onAdded = (files: File[]) => {
        this.done = false;
        this.running = false;
        this.fileQueue = files;
        this.uploadedPercent = this.createZeroArray(files.length);
    };

    private readonly onProgress = (value: number): void => {
        this.uploadedPercent[this.currentFileIndex] = value;
    };

}
